// src/pages/ClientRedeemRewardsPage.js
import React, { useState, useEffect } from 'react';
import {
    GiftIcon,
    CurrencyDollarIcon,
    CheckCircleIcon,
    XCircleIcon,
    SparklesIcon, // For voucher confirmation
} from '@heroicons/react/24/outline';

// Mock data - In a real app, this would come from an API
const mockRewards = [
    {
        id: 1,
        name: 'R20 Discount Voucher',
        description: 'Get R20 off your next purchase of R100 or more.',
        pointsNeeded: 200,
        type: 'discount',
        value: 'R20',
        imageUrl: 'https://via.placeholder.com/100x80?text=R20+Off', // Placeholder image
    },
    {
        id: 2,
        name: 'Free Medium Coffee',
        description: 'Enjoy a complimentary medium coffee of your choice.',
        pointsNeeded: 350,
        type: 'free_item',
        value: 'Medium Coffee',
        imageUrl: 'https://via.placeholder.com/100x80?text=Free+Coffee',
    },
    {
        id: 3,
        name: '10% Off Your Entire Order',
        description: 'Save 10% on your total bill (max discount R100).',
        pointsNeeded: 500,
        type: 'percentage_discount',
        value: '10%',
        imageUrl: 'https://via.placeholder.com/100x80?text=10%25+Off',
    },
    {
        id: 4,
        name: 'Exclusive Early Access',
        description: 'Get early access to our new seasonal menu.',
        pointsNeeded: 800,
        type: 'exclusive_access',
        value: 'New Menu Access',
        imageUrl: 'https://via.placeholder.com/100x80?text=VIP+Access',
    },
    {
        id: 5,
        name: 'R50 Discount Voucher',
        description: 'Get R50 off your next purchase of R200 or more.',
        pointsNeeded: 450,
        type: 'discount',
        value: 'R50',
        imageUrl: 'https://via.placeholder.com/100x80?text=R50+Off',
    },
];

const mockCustomerPoints = 450; // Current points of the customer

const ClientRedeemRewardsPage = ({ navigateTo }) => {
    const [availableRewards, setAvailableRewards] = useState([]);
    const [currentPoints, setCurrentPoints] = useState(mockCustomerPoints);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [redeemedReward, setRedeemedReward] = useState(null);

    useEffect(() => {
        // Simulate fetching rewards and customer points
        const fetchRewards = async () => {
            await new Promise(resolve => setTimeout(resolve, 500));
            setAvailableRewards(mockRewards);
            setCurrentPoints(mockCustomerPoints); // Make sure points are up-to-date
        };
        fetchRewards();
    }, []);

    const handleRedeem = (reward) => {
        if (currentPoints >= reward.pointsNeeded) {
            // Simulate API call for redemption
            console.log(`Attempting to redeem: ${reward.name}`);
            // In a real app:
            // const response = await fetch('/api/redeem-reward', {
            //   method: 'POST',
            //   headers: { 'Content-Type': 'application/json' },
            //   body: JSON.stringify({ rewardId: reward.id, customerId: 'mock_customer_id' }),
            // });
            // if (response.ok) {
            //   setCurrentPoints(prev => prev - reward.pointsNeeded);
            //   setRedeemedReward(reward);
            //   setShowConfirmation(true);
            // } else {
            //   alert('Redemption failed. Please try again.');
            // }

            // Mock success
            setCurrentPoints(prev => prev - reward.pointsNeeded);
            setRedeemedReward(reward);
            setShowConfirmation(true);
            console.log(`Successfully redeemed: ${reward.name}`);
        } else {
            alert('Not enough points to redeem this reward.');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 p-4 sm:p-6 lg:p-8 relative">
            <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-2xl p-6 sm:p-8 space-y-6 animate-fade-in">
                <div className="text-center">
                    <GiftIcon className="h-16 w-16 text-green-600 mx-auto mb-3" />
                    <h2 className="text-2xl font-extrabold text-gray-900">
                        Redeem Your Rewards
                    </h2>
                    <p className="mt-1 text-md text-gray-600">
                        Spend your points on exciting rewards!
                    </p>
                </div>

                {/* Current Points Display */}
                <div className="bg-indigo-500 text-white p-4 rounded-lg flex items-center justify-center shadow-md">
                    <CurrencyDollarIcon className="h-7 w-7 mr-2" />
                    <span className="text-3xl font-bold">{currentPoints}</span>
                    <span className="text-lg font-semibold ml-2">Points Available</span>
                </div>

                {/* Rewards List */}
                <div className="space-y-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">Available Rewards</h3>
                    {availableRewards.length > 0 ? (
                        availableRewards.map((reward) => (
                            <div
                                key={reward.id}
                                className="flex flex-col sm:flex-row items-center bg-gray-50 border border-gray-200 rounded-lg shadow-sm p-4 transition-all duration-200 hover:shadow-md"
                            >
                                <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-4 w-24 h-20 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                                    <img src={reward.imageUrl} alt={reward.name} className="object-cover w-full h-full" />
                                </div>
                                <div className="flex-grow text-center sm:text-left">
                                    <h4 className="text-lg font-semibold text-gray-800">{reward.name}</h4>
                                    <p className="text-gray-600 text-sm mt-1">{reward.description}</p>
                                </div>
                                <div className="flex-shrink-0 mt-4 sm:mt-0 sm:ml-4 text-center">
                                    <p className="text-lg font-bold text-indigo-700">
                                        {reward.pointsNeeded} Pts
                                    </p>
                                    <button
                                        onClick={() => handleRedeem(reward)}
                                        disabled={currentPoints < reward.pointsNeeded}
                                        className={`mt-2 w-full px-4 py-2 rounded-md font-medium text-white ${
                                            currentPoints >= reward.pointsNeeded
                                                ? 'bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        } transition-colors duration-200`}
                                    >
                                        {currentPoints >= reward.pointsNeeded ? 'Redeem Now' : `Need ${reward.pointsNeeded - currentPoints} more pts`}
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-600 text-center">No rewards available at the moment. Keep earning points!</p>
                    )}
                </div>

                {/* How to Redeem Instructions */}
                <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-700 p-4 rounded-md mt-6">
                    <h3 className="font-semibold text-lg mb-2">How to Redeem:</h3>
                    <ul className="list-disc list-inside text-sm space-y-1">
                        <li>Select the reward you wish to redeem above.</li>
                        <li>Confirm your redemption.</li>
                        <li>A unique voucher code or QR code will be generated for you.</li>
                        <li>Present this code to the cashier during your next purchase.</li>
                        <li>Enjoy your reward!</li>
                    </ul>
                </div>
            </div>

            {/* Redemption Confirmation Modal */}
            {showConfirmation && redeemedReward && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm w-full text-center space-y-6 animate-scale-in">
                        <SparklesIcon className="h-20 w-20 text-green-500 mx-auto" />
                        <h3 className="text-2xl font-bold text-gray-900">Reward Redeemed!</h3>
                        <p className="text-gray-700 text-lg">
                            You've successfully redeemed: <br /><strong className="text-indigo-600">{redeemedReward.name}</strong>
                        </p>
                        <p className="text-gray-600 text-sm">
                            Your points balance is now <span className="font-bold">{currentPoints}</span>.
                        </p>
                        <div className="bg-green-50 border border-green-200 p-4 rounded-md">
                            <p className="font-semibold text-green-700">Your Voucher Code:</p>
                            <p className="text-2xl font-extrabold text-green-800 mt-2 tracking-wide">
                                LOYAL{Math.random().toString(36).substr(2, 6).toUpperCase()}
                            </p>
                            <p className="text-xs text-green-600 mt-2">
                                Present this code to the cashier.
                            </p>
                        </div>
                        <button
                            onClick={() => { setShowConfirmation(false); setRedeemedReward(null); navigateTo('clientDashboard'); }}
                            className="w-full py-3 px-4 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientRedeemRewardsPage;